/**
 * SOLID Review
 * S — Strong. One concern: the account on the auth server, without the token ever leaving Infrastructure.
 * O — Good. A new auth call is one method here over the same gateway and token holder.
 * L — Strong. Implements the port interface exactly.
 * I — Good. The port's ten ops — the auth REST surface, which its screens use whole.
 * D — Good. The Infrastructure-internal REST gateway and token holder; the Application's port.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPayloadMe } from '../../domain/ports/schemas/SessionMoves';
import type {
  IPayloadEmail, IPayloadSignUp, IPayloadVerifyCode, IPayloadNickname, IPayloadEmailCode, ICommandReferral,
  IPayloadSignedIn, IPayloadProfile, IPayloadReferral,
} from '../../domain/ports/schemas/AuthMoves';
import type { IPortClientAuthServer } from '../../application/ports/contracts';
import type { IGatewayAuthRest, IEntityProfile } from './authApiService';
import { RestApiError } from '../contract/clients';
import { restCall, restFailure, type TRestResult } from '../contract/restResult';
import { getSessionToken, saveSessionToken, clearSessionToken, readSessionAccount } from '../storage/sessionTokenStorage';

/** The statuses that mean "no player behind this token", not "the server could not be asked". */
const NO_PLAYER: ReadonlySet<number> = new Set([401, 404]);
const NOT_SIGNED_IN = { code: 'NOT_SIGNED_IN', message: 'Please sign in first.' } as const;

function ok<T>(value: T): TRestResult<T> {
  return { success: true, value };
}

function profileOf(answer: IEntityProfile): IPayloadProfile {
  return { displayName: answer.displayName, email: answer.email, matchCredits: answer.matchCredits };
}

/**
 * THE AUTH GATEWAY (frontend strict-ports plan, FE2 two ops, FE3 the rest — 2026-10-03).
 *
 * THE TOKEN STAYS HERE. A code accepted is saved to the token holder and answered as WHO signed in; the
 * profile's calls read the token from the holder; signing out clears it. The Application, the Domain and
 * React see the account, never the JWT.
 *
 * FE3 also separates the two ways `me` can come back empty. FE2 moved AppRoot's `.catch(() => null)`
 * unchanged, so an offline reload read as "no player" and signed a registered player out (§28.199). Now a
 * 401 or 404 is "no player" (null), and anything else fails — the entry stops without signing out.
 */
export class ClientAuthServerAdapter implements IPortClientAuthServer {
  constructor(private readonly rest: IGatewayAuthRest) {}

  async account(_ctx: IContextRequest) {
    return ok(readSessionAccount());
  }

  async me(_ctx: IContextRequest): Promise<TRestResult<IPayloadMe | null>> {
    const jwt = getSessionToken();
    if (!jwt) return ok(null);
    try {
      const answer = await this.rest.getMe(jwt);
      const me: IPayloadMe = {
        id: answer.id, displayName: answer.displayName, email: answer.email, matchCredits: answer.matchCredits, isAdmin: answer.isAdmin,
      };
      return ok(me);
    } catch (error) {
      if (error instanceof RestApiError && NO_PLAYER.has(error.status)) return ok(null);
      return restFailure(error);
    }
  }

  async signOut(_ctx: IContextRequest) {
    clearSessionToken();
    return ok(undefined);
  }

  async requestLoginCode(_ctx: IContextRequest, request: IPayloadEmail): Promise<TRestResult<void>> {
    return restCall(async () => { await this.rest.requestLoginCode(request.email); });
  }

  async register(_ctx: IContextRequest, request: IPayloadSignUp): Promise<TRestResult<void>> {
    return restCall(async () => {
      await this.rest.register(request.email, request.displayName, request.referralCode ?? undefined, request.consentAccepted);
    });
  }

  /** A code accepted: the token is kept here, and the answer is who signed in. */
  async verifyCode(_ctx: IContextRequest, request: IPayloadVerifyCode): Promise<TRestResult<IPayloadSignedIn>> {
    try {
      const answer = request.purpose === 'VERIFY_EMAIL'
        ? await this.rest.verifyEmail(request.email, request.code)
        : await this.rest.verifyLoginCode(request.email, request.code);
      saveSessionToken(answer.jwt);
      const identity: IPayloadSignedIn = { playerId: answer.playerId, displayName: answer.displayName, matchCredits: answer.matchCredits };
      return ok(identity);
    } catch (error) {
      return restFailure(error);
    }
  }

  async validateReferral(_ctx: IContextRequest, request: ICommandReferral): Promise<TRestResult<IPayloadReferral>> {
    return restCall(async (): Promise<IPayloadReferral> => ({ valid: (await this.rest.validateReferral(request.code)).valid }));
  }

  async updateDisplayName(_ctx: IContextRequest, request: IPayloadNickname): Promise<TRestResult<IPayloadProfile>> {
    const jwt = getSessionToken();
    if (!jwt) return { success: false, error: NOT_SIGNED_IN };
    return restCall(async () => profileOf(await this.rest.updateDisplayName(jwt, request.displayName)));
  }

  async requestEmailChange(_ctx: IContextRequest, request: IPayloadEmail): Promise<TRestResult<void>> {
    const jwt = getSessionToken();
    if (!jwt) return { success: false, error: NOT_SIGNED_IN };
    return restCall(async () => { await this.rest.requestEmailChange(jwt, request.email); });
  }

  async verifyEmailChange(_ctx: IContextRequest, request: IPayloadEmailCode): Promise<TRestResult<IPayloadProfile>> {
    const jwt = getSessionToken();
    if (!jwt) return { success: false, error: NOT_SIGNED_IN };
    return restCall(async () => profileOf(await this.rest.verifyEmailChange(jwt, request.email, request.code)));
  }
}
